/**
 * Dados fictícios para o mockup do módulo Diretoria (pré-visualização).
 * Fase 2: substituir por respostas da API.
 */

export type DiretoriaChartKind = 'line' | 'bar' | 'pie' | 'none';

export interface DiretoriaKpi {
  label: string;
  value: string;
  hint?: string;
}

/** Linhas de série para gráficos line/bar: eixo X = `name` */
export interface DiretoriaSeriesPoint {
  name: string;
  valor: number;
  valor2?: number;
}

export interface DiretoriaPieSlice {
  name: string;
  value: number;
}

export interface DiretoriaIndicatorMock {
  id: string;
  title: string;
  shortDescription: string;
  kpis: DiretoriaKpi[];
  chartKind: DiretoriaChartKind;
  /** line / bar */
  series?: DiretoriaSeriesPoint[];
  /** pie */
  pieData?: DiretoriaPieSlice[];
  barSeriesKey?: 'valor' | 'valor2';
  tableHeaders: string[];
  tableRows: string[][];
  footnote: string;
}

const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

function serieBase(seed: number): DiretoriaSeriesPoint[] {
  return meses.map((name, i) => ({
    name,
    valor: Math.round(40 + seed * 3 + Math.sin(i / 2) * 18 + i * 2),
    valor2: Math.round(25 + seed * 2 + Math.cos(i / 3) * 12 + i),
  }));
}

export const DIRETORIA_INDICATORS_MOCK: DiretoriaIndicatorMock[] = [
  {
    id: 'custo-moto',
    title: 'Custo por moto',
    shortDescription: 'Histórico acumulado médio e mensal (exemplo).',
    kpis: [
      { label: 'Média mensal', value: 'R$ 1.284', hint: 'mock' },
      { label: 'Acumulado (12m)', value: 'R$ 14.892', hint: 'mock' },
      { label: 'Motos (base)', value: '186', hint: 'mock' },
    ],
    chartKind: 'line',
    series: serieBase(1),
    tableHeaders: ['Marca / modelo', 'Custo médio (mock)', 'Qtd.'],
    tableRows: [
      ['Honda CG 160', 'R$ 1.120', '64'],
      ['Yamaha Fazer 250', 'R$ 1.410', '38'],
      ['BMW G 310', 'R$ 1.980', '12'],
    ],
    footnote:
      'Definição de custo e fonte de dados serão alinhadas na fase com integração / cadastro de custos.',
  },
  {
    id: 'faturamento-bruto',
    title: 'Faturamento bruto geral',
    shortDescription: 'Anual e mensal (valores ilustrativos).',
    kpis: [
      { label: 'Anual (YTD)', value: 'R$ 8,42 mi', hint: 'mock' },
      { label: 'Mês atual', value: 'R$ 712.400', hint: 'mock' },
      { label: 'Var. vs mês ant.', value: '+6,2%', hint: 'mock' },
    ],
    chartKind: 'bar',
    series: serieBase(4).map((p) => ({ ...p, valor: p.valor * 1200, valor2: (p.valor2 ?? 0) * 800 })),
    barSeriesKey: 'valor',
    tableHeaders: ['Unidade (demo)', 'Faturamento mês'],
    tableRows: [
      ['Matriz JP', 'R$ 312.000'],
      ['Filial Natal', 'R$ 228.400'],
      ['Filial FOR', 'R$ 172.000'],
    ],
    footnote: 'Na fase de dados reais: regra contábil (recebido vs competência) e escopo por empresa.',
  },
  {
    id: 'inadimplencia',
    title: 'Inadimplência',
    shortDescription: 'Títulos a receber vencidos — acumulado e mês (exemplo).',
    kpis: [
      { label: 'Saldo vencido', value: 'R$ 184.200', hint: 'mock' },
      { label: 'Títulos vencidos', value: '127', hint: 'mock' },
      { label: 'Novos no mês', value: '23', hint: 'mock' },
    ],
    chartKind: 'bar',
    series: meses.slice(0, 8).map((name, i) => ({ name, valor: 12 + i * 3, valor2: 8 + i * 2 })),
    barSeriesKey: 'valor',
    tableHeaders: ['Faixa de atraso', 'Qtd.', 'Saldo (mock)'],
    tableRows: [
      ['0–30 dias', '42', 'R$ 58.200'],
      ['31–60 dias', '28', 'R$ 44.100'],
      ['61+ dias', '57', 'R$ 81.900'],
    ],
    footnote: 'Aging e totais seguirão a mesma base usada em relatórios de cobrança.',
  },
  {
    id: 'infracoes',
    title: 'Infrações (multas)',
    shortDescription: 'Pagas, vencidas e a vencer — acumulado e mês (exemplo).',
    kpis: [
      { label: 'Pagas (mês)', value: '34', hint: 'mock' },
      { label: 'Vencidas', value: '19', hint: 'mock' },
      { label: 'A vencer', value: '41', hint: 'mock' },
    ],
    chartKind: 'line',
    series: serieBase(2).map((p) => ({ ...p, valor: p.valor + 5, valor2: p.valor2 })),
    tableHeaders: ['Estado', 'Qtd. (mock)'],
    tableRows: [
      ['Pagas', '128'],
      ['Vencidas', '44'],
      ['A vencer', '62'],
    ],
    footnote: 'Classificação de multa poderá replicar a heurística já usada no app (descrição / observações).',
  },
  {
    id: 'motos-alugadas',
    title: 'Motos alugadas',
    shortDescription: 'Acumulado e fluxo mensal (exemplo).',
    kpis: [
      { label: 'Ativas (est.)', value: '412', hint: 'mock' },
      { label: 'Novas no mês', value: '38', hint: 'mock' },
      { label: 'Encerradas no mês', value: '29', hint: 'mock' },
    ],
    chartKind: 'bar',
    series: serieBase(3),
    barSeriesKey: 'valor',
    tableHeaders: ['Mês', 'Entradas', 'Saídas'],
    tableRows: [
      ['Mês atual', '38', '29'],
      ['Mês anterior', '33', '31'],
    ],
    footnote: 'Critérios de “ativa” virão de locações importadas / ERP.',
  },
  {
    id: 'rentabilidade-moto',
    title: 'Rentabilidade média por moto',
    shortDescription: 'Histórico e anual (receita vs custo — exemplo).',
    kpis: [
      { label: 'Média anual', value: 'R$ 2.140 / moto', hint: 'mock' },
      { label: 'Últimos 12m', value: 'R$ 1.980 / moto', hint: 'mock' },
      { label: 'Margem (demo)', value: '18,5%', hint: 'mock' },
    ],
    chartKind: 'line',
    series: meses.map((name, i) => ({
      name,
      valor: 1500 + i * 45 + (i % 3) * 20,
      valor2: 900 + i * 30,
    })),
    tableHeaders: ['Componente', 'Valor (mock)'],
    tableRows: [
      ['Receita atribuída', 'R$ 3,82 mi'],
      ['Custos atribuídos', 'R$ 3,12 mi'],
    ],
    footnote: 'Com custos reais: receita ÷ motos e comparação com período anterior.',
  },
  {
    id: 'motos-disponiveis',
    title: 'Motos disponíveis',
    shortDescription: 'Por marca / modelo (exemplo).',
    kpis: [
      { label: 'Disponíveis hoje', value: '94', hint: 'mock' },
      { label: 'Top marca', value: 'Honda (38)', hint: 'mock' },
      { label: 'Em manutenção', value: '12', hint: 'mock' },
    ],
    chartKind: 'bar',
    series: [
      { name: 'Honda', valor: 38 },
      { name: 'Yamaha', valor: 24 },
      { name: 'BMW', valor: 14 },
      { name: 'Kawasaki', valor: 11 },
      { name: 'Outras', valor: 7 },
    ],
    barSeriesKey: 'valor',
    tableHeaders: ['Modelo', 'Disponíveis'],
    tableRows: [
      ['CG 160', '22'],
      ['Fazer 250', '14'],
      ['G 310 R', '9'],
    ],
    footnote: '“Disponível” seguirá o mapeamento de status do equipamento nos dados importados.',
  },
  {
    id: 'motos-status',
    title: 'Motos por status',
    shortDescription: 'Distribuição atual (exemplo).',
    kpis: [
      { label: 'Locada', value: '58%', hint: 'mock' },
      { label: 'Disponível', value: '23%', hint: 'mock' },
      { label: 'Outros', value: '19%', hint: 'mock' },
    ],
    chartKind: 'pie',
    pieData: [
      { name: 'Locada', value: 240 },
      { name: 'Disponível', value: 94 },
      { name: 'Manutenção', value: 38 },
      { name: 'Reserva', value: 40 },
    ],
    tableHeaders: ['Status', 'Qtd.'],
    tableRows: [
      ['Locada', '240'],
      ['Disponível', '94'],
      ['Manutenção', '38'],
      ['Reserva', '40'],
    ],
    footnote: 'Agrupamento por status atual da locação / equipamento.',
  },
  {
    id: 'locacao-local',
    title: 'Locação por bairro / cidade',
    shortDescription: 'Somatório por local do locatário (exemplo).',
    kpis: [
      { label: 'Cidades (demo)', value: '6', hint: 'mock' },
      { label: 'Top cidade', value: 'João Pessoa', hint: 'mock' },
      { label: 'Contratos (mês)', value: '38', hint: 'mock' },
    ],
    chartKind: 'bar',
    series: [
      { name: 'João Pessoa', valor: 142 },
      { name: 'Natal', valor: 98 },
      { name: 'Fortaleza', valor: 76 },
      { name: 'Recife', valor: 54 },
    ],
    barSeriesKey: 'valor',
    tableHeaders: ['Bairro', 'Cidade', 'Locações (mock)'],
    tableRows: [
      ['Bessa', 'João Pessoa', '28'],
      ['Lagoa Nova', 'Natal', '22'],
      ['Aldeota', 'Fortaleza', '19'],
    ],
    footnote: 'Exige endereço do locatário na base; pode vir de importação ERP em fase futura.',
  },

];

export const DIRETORIA_DEMO_COMPANIES = [
  { id: 'all', label: 'Todas as unidades (demo)' },
  { id: 'jp', label: 'Matriz — João Pessoa' },
  { id: 'natal', label: 'Filial — Natal' },
] as const;

export const DIRETORIA_DEMO_PERIODS = [
  { id: 'mes', label: 'Mês atual' },
  { id: 'ano', label: 'Mês Passado' },
  { id: '12m', label: 'Últimos 12 meses' },
] as const;
